import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

/**
 * New UI migration — Phase 1 (see project doc claude/new-ui-migration-phase1-plan.md).
 *
 * Lets the user hop to the new Next.js UI (and back) without logging in again:
 * the backend issues a single-use handoff code, the target UI exchanges it.
 */
export interface UiMigrationConfig {
  can_switch: boolean;
  preferred_ui: 'legacy' | 'new' | null;
  new_ui_url: string;
  legacy_ui_url: string;
}

/** Legacy route prefix → new UI path. Longest prefix wins; unmapped routes go to the dashboard. */
const LEGACY_TO_NEW_ROUTES: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  [/^\/dashboard(\/|$)/, () => '/app/dashboard'],
  [/^\/leads\/pipelines(\/|$)/, () => '/app/leads'],
  [/^\/leads\/([^/?#]+)/, (m) => `/app/leads/${m[1]}`],
  [/^\/leads(\/|$)/, () => '/app/leads'],
  [/^\/reports?(\/|$)/, () => '/app/reports'],
  [/^\/workspaces(\/|$)/, () => '/app/workspaces'],
  [/^\/icp(\/|$)/, () => '/app/ideal-customer'],
  [/^\/advanced-search(\/|$)/, () => '/app/search'],
  [/^\/crm(\/|$)/, () => '/app/crm'],
  [/^\/exports(\/|$)/, () => '/app/exports'],
  [/^\/settings(\/|$)/, () => '/app/settings'],
];

export function mapLegacyPathToNewUi(legacyUrl: string): string {
  const path = (legacyUrl || '/').split(/[?#]/)[0];
  for (const [pattern, toNew] of LEGACY_TO_NEW_ROUTES) {
    const match = path.match(pattern);
    if (match) {
      return toNew(match);
    }
  }
  // Dropped legacy-only screens (psy-testing, market-insight, leads-legacy, …) and anything else.
  return '/app/dashboard';
}

@Injectable({ providedIn: 'root' })
export class UiSwitchService {
  private readonly config$ = new BehaviorSubject<UiMigrationConfig | null>(null);
  private loaded = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private authService: AuthService,
  ) {
    // Login / handoff responses already carry the block — use it right away.
    this.authService.authUser().subscribe((user) => {
      if (user?.ui_migration) {
        this.config$.next(user.ui_migration);
      }
    });
  }

  /** Current config; fetched once from /users/me so sessions that predate the feature also get it. */
  config(): Observable<UiMigrationConfig | null> {
    if (!this.loaded) {
      this.loaded = true;
      this.http
        .get<any>(`${environment.api.url}/users/me`)
        .pipe(
          map((me) => (me?.ui_migration ?? null) as UiMigrationConfig | null),
          catchError(() => of(null)),
          take(1),
        )
        .subscribe((cfg) => {
          if (cfg) {
            this.config$.next(cfg);
          }
        });
    }
    return this.config$.asObservable();
  }

  /** Ask the backend for a handoff code and send the browser to the new UI. */
  async switchToNewUi(): Promise<void> {
    const path = mapLegacyPathToNewUi(this.router.url);
    const response = await firstValueFrom(
      this.http.post<any>(`${environment.api.url}/ui/handoff`, { target: 'new', path }),
    );
    const redirectUrl: string | undefined = response?.data?.redirect_url;
    if (!redirectUrl) {
      throw new Error('Missing redirect_url');
    }
    window.location.href = redirectUrl;
  }

  /** Exchange a handoff code (we are the target UI). Returns the login-shaped payload. */
  exchange(code: string): Promise<{ token: string; user: any; path: string }> {
    return firstValueFrom(
      this.http.post<{ token: string; user: any; path: string }>(`${environment.api.url}/ui/handoff/exchange`, {
        code,
        target: 'legacy',
      }),
    );
  }
}
